// The send / schedule panel of a Friday report (Reports / Ops UX pass 2026-10-21; Mailchimp's
// send flow in a right drawer): Recipients (the version's fixed list once it is frozen, else the
// members and extra addresses the server would use, each one a checkbox: unticking sends to the
// rest through the API's `recipients` override), Send (the blocked reason, the stale notice with
// Send anyway, the one primary button; once sent, when and to whom, Resend, the operator's
// Reconcile), and Schedule (every Friday at the company's time, sent by itself two hours later
// or by a manager: `PATCH /companies/{co}` spreading the stored settings). Two calls when it
// opens (the company, the first page of people); none on the report page's load.
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
  ToggleRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyDetail, ReportListItem } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { LinkButton } from "../ops-ux";
import { companyQuery, keys, peopleQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";

const REPORT_ROLES = ["owner", "admin", "client_viewer"];

type Recipient = ReportListItem["recipients"][number];

export function sendBlockedReason(record: ReportListItem, stale: boolean): string | null {
  if (record.state === "draft") return "Generate this version before sending it.";
  if (record.state === "sent") return "This version has been sent.";
  if (stale) return "The data changed after this version was generated. Regenerate it, then send.";
  return null;
}

/** What is known about this version's delivery (the version's state; no per-recipient ledger). */
function recipientState(record: ReportListItem): {
  label: string;
  variant: "success" | "danger" | "neutral";
} {
  if (record.state === "sent") return { label: "Sent", variant: "success" };
  if (record.state === "failed") return { label: "Failed", variant: "danger" };
  return { label: "Not sent", variant: "neutral" };
}

export function ReportSendDrawer({
  api,
  company,
  week,
  record,
  stale,
  open,
  onOpenChange,
  onStale,
  canAct,
  operator,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
  record: ReportListItem | null;
  stale: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStale: () => void;
  canAct: boolean;
  operator: boolean;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="600px">
      {open ? <DrawerHeader>Reports › {week} › Send</DrawerHeader> : null}
      {open ? (
        <DrawerBody data-slot="report-send">
          <DrawerTitle>Send Friday report · {week}</DrawerTitle>
          {record ? (
            <SendBody
              api={api}
              company={company}
              record={record}
              stale={stale}
              onStale={onStale}
              canAct={canAct}
              operator={operator}
            />
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              No version of {week} yet: generate it first.
            </p>
          )}
          <ScheduleSection api={api} company={company} canAct={canAct} />
        </DrawerBody>
      ) : null}
    </Drawer>
  );
}

function SendBody({
  api,
  company,
  record,
  stale,
  onStale,
  canAct,
  operator,
}: {
  api: Api;
  company: CompanyAccess;
  record: ReportListItem;
  stale: boolean;
  onStale: () => void;
  canAct: boolean;
  operator: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: keys.reports(co) });
  const frozen = record.frozen_at !== null;
  const detail = useQuery({ ...companyQuery(api, co), enabled: !frozen });
  const people = useInfiniteQuery({ ...peopleQuery(api, co), enabled: !frozen });
  // The recipients the server would use now (Setup › General's rule), one checkbox each.
  const defaults: Recipient[] = frozen
    ? record.recipients
    : [
        ...(people.data?.pages.flatMap((p) => p.data) ?? [])
          .filter(
            (p) =>
              p.status === "active" &&
              p.email &&
              p.roles.some((r) => REPORT_ROLES.includes(r)) &&
              !(detail.data?.settings.friday_report?.opted_out_membership_ids ?? []).includes(p.id),
          )
          .map((p) => ({ email: p.email ?? "", membership_id: p.id, kind: "member" as const })),
        ...(detail.data?.settings.friday_report?.recipients_extra ?? []).map((email) => ({
          email,
          kind: "extra" as const,
        })),
      ];
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const chosen = defaults.filter((r) => !off.has(r.email));
  const state = recipientState(record);
  const blocked = sendBlockedReason(record, stale);
  const send = useMutation({
    mutationFn: () =>
      api.client.reports.send({
        params: { co, id: record.id },
        // The server's own list unless some were unticked (then the rest, by override).
        body: off.size ? { recipients: chosen } : {},
      }),
    onSuccess: refresh,
    onError: (e) => {
      if (e instanceof ApiError && e.code === "report_stale") onStale();
    },
  });
  const loading = !frozen && (detail.isPending || people.isPending);
  return (
    <>
      <p className="mt-1 text-sm text-muted-foreground">
        Version {record.version} ·{" "}
        {record.sent_at
          ? `sent ${formatDateTime(record.sent_at)}`
          : record.generated_at
            ? `generated ${formatDateTime(record.generated_at)}`
            : "not generated"}
      </p>
      <DrawerSection
        title={frozen ? "Recipients" : `Recipients · ${chosen.length} of ${defaults.length}`}
        action={
          canAct ? (
            <LinkButton
              size="xs"
              render={
                <Link
                  to="/c/$slug/setup/general"
                  params={{ slug: company.slug }}
                  search={keepFocus}
                  hash="friday-report"
                />
              }
            >
              Edit in Setup
            </LinkButton>
          ) : null
        }
      >
        {loading ? (
          <Loading label="Loading recipients" rows={2} />
        ) : detail.isError ? (
          <LoadError what="Recipients" error={detail.error} />
        ) : defaults.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-slot="recipients">
            Nobody gets this report yet: add owners, admins or client viewers, or extra addresses in
            Setup › General.
          </p>
        ) : (
          <ul data-slot="recipients" className="flex flex-col">
            {defaults.map((r) => (
              <li
                key={r.email}
                className="flex h-9 items-center gap-2.5 border-b border-subtle text-sm"
                data-recipient={r.email}
              >
                {frozen || !canAct ? null : (
                  <Checkbox
                    aria-label={`Send to ${r.email}`}
                    checked={!off.has(r.email)}
                    onCheckedChange={(on) =>
                      setOff((s) => {
                        const n = new Set(s);
                        if (on) n.delete(r.email);
                        else n.add(r.email);
                        return n;
                      })
                    }
                  />
                )}
                <span className="min-w-0 flex-1 truncate">{r.email}</span>
                <span className="text-xs text-muted-foreground">
                  {r.kind === "extra" ? "Extra" : "Member"}
                </span>
                {frozen ? <Badge variant={state.variant}>{state.label}</Badge> : null}
              </li>
            ))}
          </ul>
        )}
        {frozen ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Fixed by the first send. The state is the version's; delivery per recipient is not
            reported yet.
          </p>
        ) : null}
      </DrawerSection>

      <DrawerSection title={record.state === "sent" ? "Sent" : "Send"} data-slot="report-actions">
        {record.send_error ? (
          <Notice tone="danger" title="The last send failed" className="mb-3">
            {record.send_error}
          </Notice>
        ) : null}
        {stale && record.state !== "sent" && record.state !== "draft" ? (
          <Notice
            tone="warning"
            title="The report is stale"
            className="mb-3"
            data-slot="report-stale"
            action={canAct ? <ForceSendDialog api={api} co={co} record={record} /> : null}
          >
            Hours, costs or numbers of this week changed after it was generated. Regenerate to send
            the current figures.
          </Notice>
        ) : null}
        {send.isError && !(send.error instanceof ApiError && send.error.code === "report_stale") ? (
          <FieldError className="mb-3">
            {problemText(send.error, "The report could not be sent.")}
          </FieldError>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          {canAct && record.state !== "sent" ? (
            <Button
              size="sm"
              disabled={!!blocked || send.isPending || chosen.length === 0}
              aria-describedby="report-send-why"
              onClick={() => send.mutate()}
            >
              {send.isPending
                ? "Sending…"
                : `Send report${chosen.length ? ` to ${chosen.length}` : ""}`}
            </Button>
          ) : null}
          {canAct && record.state === "sent" ? (
            <ResendDialog api={api} co={co} record={record} />
          ) : null}
          {operator && record.frozen_at ? (
            <ReconcileDialog api={api} co={co} record={record} />
          ) : null}
          <span id="report-send-why" role="status" className="text-xs text-muted-foreground">
            {record.state === "sent" && record.sent_at
              ? `Sent ${formatDateTime(record.sent_at)} to ${record.recipients.length} ${record.recipients.length === 1 ? "recipient" : "recipients"}.`
              : (blocked ?? "")}
          </span>
        </div>
      </DrawerSection>
    </>
  );
}

/** Every Friday at the company's time; sent by itself two hours later, or by a manager. */
function ScheduleSection({
  api,
  company,
  canAct,
}: {
  api: Api;
  company: CompanyAccess;
  canAct: boolean;
}) {
  const detail = useQuery(companyQuery(api, company.id));
  if (detail.isPending)
    return (
      <DrawerSection title="Schedule">
        <Loading label="Loading the schedule" rows={1} />
      </DrawerSection>
    );
  if (detail.isError)
    return (
      <DrawerSection title="Schedule">
        <LoadError what="The schedule" error={detail.error} />
      </DrawerSection>
    );
  return <ScheduleForm api={api} detail={detail.data} canAct={canAct} />;
}

function ScheduleForm({
  api,
  detail,
  canAct,
}: {
  api: Api;
  detail: CompanyDetail;
  canAct: boolean;
}) {
  const qc = useQueryClient();
  const stored = detail.settings.friday_report;
  const [time, setTime] = useState(stored?.generate_at ?? "15:00");
  const [auto, setAuto] = useState(stored?.auto_send ?? false);
  const id = useId();
  const save = useMutation({
    // REPORTS-UI: spread the settings as stored now (a member may have opted out since this
    // drawer loaded them), changing only the schedule.
    mutationFn: async () => {
      const fresh = await api.client.companies.get({ params: { co: detail.id } });
      return api.client.companies.update({
        params: { co: detail.id },
        body: {
          settings: {
            ...fresh.settings,
            friday_report: { ...fresh.settings.friday_report, generate_at: time, auto_send: auto },
          },
        },
      });
    },
    onSuccess: (company) => qc.setQueryData(keys.company(detail.id), company),
  });
  const changed =
    time !== (stored?.generate_at ?? "15:00") || auto !== (stored?.auto_send ?? false);
  return (
    <DrawerSection title="Schedule" data-slot="report-schedule">
      <p className="mb-3 text-sm text-muted-foreground" data-slot="schedule-line">
        Every Friday at {stored?.generate_at ?? "15:00"} ({detail.calendar_config.timezone}),{" "}
        {stored?.auto_send ? "sent by itself two hours later." : "sent by a manager from here."}
      </p>
      {canAct ? (
        <div className="flex flex-col gap-3">
          <Field className="w-auto">
            <FieldLabel htmlFor={id}>Generate at</FieldLabel>
            <Input
              id={id}
              type="time"
              className="w-36"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </Field>
          <ToggleRow
            label="Send automatically two hours later"
            checked={auto}
            onCheckedChange={setAuto}
            className="py-0"
          />
          {save.isError ? (
            <FieldError>{problemText(save.error, "The schedule could not be saved.")}</FieldError>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            className="self-start"
            disabled={!changed || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save schedule"}
          </Button>
        </div>
      ) : null}
    </DrawerSection>
  );
}

function ForceSendDialog({ api, co, record }: { api: Api; co: string; record: ReportListItem }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const send = useMutation({
    mutationFn: () =>
      api.client.reports.send({ params: { co, id: record.id }, body: { force: true } }),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: keys.reports(co) });
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Send anyway
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Send version {record.version} as it is?</AlertDialogTitle>
          <AlertDialogDescription>
            Its figures predate the latest hours, costs or numbers. Recipients get this version; the
            change is recorded as a forced send.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {send.isError ? (
          <FieldError>{problemText(send.error, "The report could not be sent.")}</FieldError>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button disabled={send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Sending…" : "Send anyway"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ResendDialog({ api, co, record }: { api: Api; co: string; record: ReportListItem }) {
  const [open, setOpen] = useState(false);
  // One key per resend: a retry of the same request delivers at most once more (the server binds
  // the key to this resend operation).
  const [key, setKey] = useState(newIdempotencyKey);
  const qc = useQueryClient();
  const resend = useMutation({
    mutationFn: () =>
      api.client.reports.send({
        params: { co, id: record.id },
        idempotencyKey: key,
        body: { force: true },
      }),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: keys.reports(co) });
    },
  });
  const n = record.recipients.length;
  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setKey(newIdempotencyKey());
      }}
    >
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Resend
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resend version {record.version}?</AlertDialogTitle>
          <AlertDialogDescription>
            Delivers the same report once more to all {n} {n === 1 ? "recipient" : "recipients"} of
            this version, for someone who lost it. Email cannot be recalled.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {resend.isError ? (
          <FieldError>{problemText(resend.error, "The report could not be resent.")}</FieldError>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button disabled={resend.isPending} onClick={() => resend.mutate()}>
            {resend.isPending ? "Resending…" : "Resend"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const OPERATION = /^(send|resend:[0-9a-f-]{36})$/;

function ReconcileDialog({ api, co, record }: { api: Api; co: string; record: ReportListItem }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(record.recipients[0]?.email ?? "");
  const [operation, setOperation] = useState("send");
  const [outcome, setOutcome] = useState<"delivered" | "not_delivered">("delivered");
  const [evidence, setEvidence] = useState<"provider_log" | "message_id_lookup">("provider_log");
  const [note, setNote] = useState("");
  const ids = { email: useId(), op: useId(), outcome: useId(), evidence: useId(), note: useId() };
  const qc = useQueryClient();
  const reconcile = useMutation({
    mutationFn: () =>
      api.client.reports.reconcile({
        params: { co, id: record.id },
        body: { email, operation, outcome, evidence, note: note.trim() },
      }),
    onSuccess: () => {
      setOpen(false);
      setNote("");
      void qc.invalidateQueries({ queryKey: keys.reports(co) });
    },
  });
  const opValid = OPERATION.test(operation);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (opValid && note.trim().length >= 10) reconcile.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Reconcile
      </Button>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Reconcile a delivery</DialogTitle>
            <DialogDescription>
              Settles a delivery whose outcome is unknown (the mail session broke after submission)
              from the mail provider's records. Only an unconfirmed delivery can be settled; the
              evidence is recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.email}>Recipient</FieldLabel>
              <NativeSelect id={ids.email} value={email} onChange={(e) => setEmail(e.target.value)}>
                {record.recipients.map((r) => (
                  <NativeSelectOption key={r.email} value={r.email}>
                    {r.email}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field data-invalid={!opValid}>
              <FieldLabel htmlFor={ids.op}>Operation</FieldLabel>
              <Input
                id={ids.op}
                value={operation}
                aria-invalid={!opValid}
                onChange={(e) => setOperation(e.target.value.trim())}
              />
              <FieldDescription>
                "send" for the first send, or "resend:" and the resend's id from the alert email.
              </FieldDescription>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor={ids.outcome}>Outcome</FieldLabel>
                <NativeSelect
                  id={ids.outcome}
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value as typeof outcome)}
                >
                  <NativeSelectOption value="delivered">Delivered</NativeSelectOption>
                  <NativeSelectOption value="not_delivered">Not delivered</NativeSelectOption>
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.evidence}>Evidence</FieldLabel>
                <NativeSelect
                  id={ids.evidence}
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value as typeof evidence)}
                >
                  <NativeSelectOption value="provider_log">Provider log</NativeSelectOption>
                  <NativeSelectOption value="message_id_lookup">
                    Message-ID lookup
                  </NativeSelectOption>
                </NativeSelect>
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor={ids.note}>What the evidence showed</FieldLabel>
              <Textarea
                id={ids.note}
                rows={3}
                minLength={10}
                maxLength={2000}
                required
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <FieldDescription>At least 10 characters: the log line or query.</FieldDescription>
            </Field>
            {reconcile.isError ? (
              <FieldError>
                {problemText(reconcile.error, "The delivery could not be reconciled.")}
              </FieldError>
            ) : null}
          </FieldGroup>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button
              type="submit"
              disabled={reconcile.isPending || !opValid || note.trim().length < 10 || !email}
            >
              {reconcile.isPending ? "Saving…" : "Reconcile"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
